import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import {
  FormFieldList,
  SfSelectFormField,
  SfUtils,
  SmartFormComponent,
} from 'ngx-smart-form';
import { FormField } from 'ngx-smart-form/lib/smart-form/classes/form-field';
import { TitlePercentageComponent } from '../_widgets/title-percentage/title-percentage.component';
import { SharedModule } from '../_shared/shared.module';
import {
  catchError,
  debounceTime,
  forkJoin,
  map,
  mergeMap,
  Observable,
  of,
  skip,
  Subscription,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { TaxDataComponent } from './tax-data/tax-data.component';
import { FullScreenDialogConfig } from '../_config/full-screen-dialog-config';
import { MatDialog } from '@angular/material/dialog';
import { getImpresaFields } from './fields/impresa-fields';
import { getConsentFields } from './fields/consent-fields';
import { PraticaProgressService } from '../_core/services/pratica-progress.service';
import { ActivatedRoute } from '@angular/router';
import { PersonaGiuridica } from './models/persona-giuridica';
import { PropertyTableComponent } from '../anagraphics/property-table/property-table.component';
import { DialogService } from '../_core/services/dialog.service';
import { AddPropertyDialogComponent } from '../anagraphics/add-property-dialog/add-property-dialog.component';
import { Property } from '../anagraphics/property';
import { PropertyService } from '../_core/services/property.service';
import { MessageDialogComponent } from '../_shared/message-dialog/message-dialog.component';
import { ConfirmDialogConfig } from '../_config/confirm-dialog-config';
import { KycComponent } from '../anagraphics/kyc/kyc.component';
import { CmsService } from '../_core/services/cms.service';
import { getLiberoProfessionistFields } from './fields/libero-professionista-fields';
import {
  LiberoProfessionista,
  SituazioneAbitativaEnum,
} from './models/libero-professionista';
import { StatoPratica, Target } from '../../interfaces/pratica';
import { Impresa } from './models/impresa';
import { PraticaSection } from '../pratica/models/pratica-section';
import { AddressFormComponent } from '../_widgets/address-form/address-form.component';
import { AddressFormData } from '../_widgets/address-form/address-form-data';
import { PersonaGiuridicaService } from '../_core/services/persona-giuridica.service';
import {
  FormControl,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { BucketService } from '../_core/services/bucket.service';
import { DocumentsService } from '../_core/services/documents.service';
import { crifValidator } from './fields/crif-validator';
import { ProductType } from '../requests/models/product';
import { SfAddressField } from '../_core/models/sf-address-field';
import { MappedCmsSectionWithFieldList } from '../_core/models/cms-field';
import { getApplicantFields } from './applicant-fields';
import { ShareholdersTableComponent } from './shareholders-table/shareholders-table.component';
import { Shareholder } from './shareholders-table/shareholder';
import {
  COUNTRIES,
  getCountriesOptions,
} from '../../assets/aml-config/countries';
import { AreaGeograficaField } from './fields/area-geografica-field';
import { ATECO_ATTIVITA_FINANZIARIE_ASSICURATIVE } from './ateco-attivita-finanziarie-assicurative';
import { INTERMEDIARIO_OPTIONS } from './fields/intermediario-options';
import { NATURA_GIURIDICA_OPTIONS } from './fields/natura-giuridica-options';
import { EditableField } from '../_widgets/address-form/editable-field';
import { getExtensionLocationFields } from './extension-location-fields';
import { getSedeLegaleLocationFields } from './sede-legale-location-fields';
import { AttivitaSvoltaField } from './fields/attivita-svolta-field';
import { getModalitaIdentificazioneOptions } from './models/modalita-identificazione.enum';
import { MatExpansionModule } from '@angular/material/expansion';

@Component({
  standalone: true,
  imports: [
    SharedModule,
    TitlePercentageComponent,
    TaxDataComponent,
    PropertyTableComponent,
    KycComponent,
    AddressFormComponent,
    ShareholdersTableComponent,
    MatExpansionModule,
  ],
  selector: 'app-applicant',
  templateUrl: './applicant.component.html',
  styleUrls: ['./applicant.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ApplicantComponent implements AfterViewInit, OnDestroy {
  @ViewChildren(SmartFormComponent)
  smartFormComponentList?: QueryList<SmartFormComponent>;

  @ViewChildren('applicantForms')
  applicantForms?: QueryList<SmartFormComponent>;

  @ViewChild(KycComponent) kycComponent?: KycComponent;
  @ViewChildren(AddressFormComponent)
  addressFormComponentList?: QueryList<AddressFormComponent>;
  /*   @ViewChild(TaxDataComponent)
  taxDataComponent?: TaxDataComponent; */

  @ViewChildren('extensionsForms')
  extensionsForms?: QueryList<SmartFormComponent>;
  @ViewChildren('extensionsAddressForms')
  extensionsAddressForms?: QueryList<AddressFormComponent>;

  kycFields: { description: string; fields: FormFieldList }[] = [];

  title: string;

  fields1: FormFieldList = [];
  fields2: FormFieldList = [];

  consentFields: FormField[][] = [];
  idPratica: string = '';
  saveSubscription?: Subscription;
  personaGiuridica?: Impresa | LiberoProfessionista;
  properties: Property[] = [];

  readonly extensionsLocationFieldsToBeShown: Record<
    string,
    FormFieldList<AddressFormData>
  > = {};

  readonly sedeLegaleLocationFieldsToBeShown: FormFieldList<AddressFormData>;

  panelOpenState = false;

  applicantFormCompletionPercentage = 0;

  protected impresaValidator: ValidatorFn | undefined;

  sections: MappedCmsSectionWithFieldList[] = [];

  get showCrifError() {
    let isInterestedState =
      this.praticaProgressService.selectedPratica?.state ===
      StatoPratica.APERTA;
    let hasCrifError = this.applicantForms?.get(0)?.form?.hasError('crifError');
    return isInterestedState && hasCrifError;
  }

  get target() {
    return this.praticaProgressService.selectedPratica?.target;
  }

  get type() {
    return this.praticaProgressService.selectedPratica?.type;
  }

  constructor(
    public praticaProgressService: PraticaProgressService,
    private dialog: MatDialog,
    private activatedRoute: ActivatedRoute,
    private dialogService: DialogService,
    private propertyService: PropertyService,
    private cmsService: CmsService,
    private cdr: ChangeDetectorRef,
    private pgService: PersonaGiuridicaService,
    private documentsService: DocumentsService
  ) {
    this.title = this.type === 'AML' ? 'Cliente' : 'Richiedente';

    this.sedeLegaleLocationFieldsToBeShown = getSedeLegaleLocationFields(
      this.type
    );
  }

  ngAfterViewInit(): void {
    if (!this.praticaProgressService.selectedPratica) return;
    this.idPratica = this.idPratica =
      this.praticaProgressService.selectedPratica.id || '';

    let personaGiuridicaRequest$ = this.pgService.getById(
      this.idPratica,
      this.praticaProgressService.selectedPratica.target
    );

    personaGiuridicaRequest$
      .pipe(
        tap(() => this.subscribeToPraticaEvents()),
        switchMap((personaGiuridica) => {
          this.personaGiuridica = personaGiuridica;
          return this.buildForms(personaGiuridica);
        }),
        tap(() => this.performChecks()),
        switchMap(() => this.updatePropertyTable()),
        tap(() =>
          this.applicantForms
            ?.toArray()
            .forEach((c) => c.form.patchValue(this.personaGiuridica!))
        ),
        tap(() => {
          let isSpin = this.activatedRoute.snapshot.queryParamMap.get('spin');
          if (isSpin === 'true') {
            this.praticaProgressService.hasJustBeenSigned.next(true);
          }
        }),
        tap(() => this.trackProgress()),
        //switchMap((_) => this.checkTaxDataCompletion(this.personaGiuridica!)),
        catchError((err) => this.interceptErrors(err))
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.saveSubscription?.unsubscribe();
  }

  openPropertyForm(property?: Property) {
    let dialogRef = this.dialogService.open<
      AddPropertyDialogComponent,
      AddPropertyDialogComponent['data']
    >(AddPropertyDialogComponent, {
      ...FullScreenDialogConfig,
      data: {
        formVisible: true,
        personaGiuridica: this.personaGiuridica,
        property: property,
      },
    });

    dialogRef
      .afterClosed()
      .pipe(
        switchMap((formData) =>
          formData ? this.saveProperty(formData, property?.id) : of(undefined)
        )
      )
      .subscribe();
  }

  onDeleteProperty(event: Property) {
    this.openConfirmDialog()
      .pipe(
        switchMap((confirm) => (confirm ? this.onDelete(event.id) : of(null)))
      )
      .subscribe();
  }

  private subscribeToPraticaEvents() {
    this.saveSubscription = this.praticaProgressService.triggerSave$
      .pipe(
        mergeMap(() => {
          return this.save();
        })
      )
      .subscribe((_) => {
        this.praticaProgressService.notifySectionUpdate();
      });
  }

  private trackProgress() {
    let sfCList = this.smartFormComponentList?.toArray() || [];
    let sfKycList = this.kycComponent?.smartFormComponentList?.toArray() || [];
    let formCompList = [...sfCList, ...sfKycList];
    if (this.addressFormComponentList) {
      formCompList.push(
        ...this.addressFormComponentList.map((c) => c.smartFormComponent!)
      );
    }
    this.praticaProgressService
      .trackFormChanges(formCompList)
      .subscribe((res) => this.updateCompletionPercentage(res));
  }

  private updateCompletionPercentage(completionPercentage: number) {
    let pSFormWeight =
      /*  this.personaGiuridica?.tipoSoggetto !== 'SC'

        ? 12 / 15
        : */ 14 / 15;
    this.applicantFormCompletionPercentage =
      this.praticaProgressService.calcComplexCompletionPercentage(
        completionPercentage,
        pSFormWeight
      );
    this.calcTotalPercentage();
  }

  private calcTotalPercentage() {
    let totalProgress =
      this.applicantFormCompletionPercentage /* +
      (this.taxDataComponent?.completionPercentage.value || 0) */ +
      this.immobiliPercentage;
    this.updateProgress(totalProgress);
  }

  private updateProgress(totalProgress: number) {
    this.praticaProgressService.updateProgress(
      PraticaSection.APPLICANT,
      totalProgress > 100 ? 100 : totalProgress
    );
    this.cdr.detectChanges();
  }

  get immobiliPercentage() {
    let immobiliPercentage =
      this.praticaProgressService.calcComplexCompletionPercentage(
        this.isImmobiliValid || this.type === 'AML' ? 100 : 0,
        1 / 15
      );
    return immobiliPercentage;
  }

  get isImmobiliValid() {
    let isImmobiliValid =
      this.properties.length > 0 ||
      SfUtils.getField('situazioneAbitativa', this.fields2)?.controlRef
        ?.value !== SituazioneAbitativaEnum.PROPRIETA
        ? true
        : false;
    return isImmobiliValid;
  }

  private save(
    shareholders?: Shareholder[]
  ): Observable<object | null | undefined> {
    if (this.smartFormComponentList && this.personaGiuridica) {
      let documentazioneNumeroDipendenti = SfUtils.getField<Impresa>(
        'documentazioneNumeroDipendenti',
        this.fields1
      );
      let file = documentazioneNumeroDipendenti?.controlRef?.value;
      let data: Partial<PersonaGiuridica> = SfUtils.aggregateFormsValues(
        this.applicantForms?.toArray()
      );
      let extension = SfUtils.aggregateFormsValues(
        this.extensionsForms?.toArray(),
        true
      );
      let addressFields = this.sections
        .map((s) => s.fields)
        .flat(2)
        .filter((f) => f instanceof SfAddressField);
      let extensionLocation = {};
      this.extensionsAddressForms?.forEach((c, i) => {
        extensionLocation[addressFields[i].formControlName] =
          c.addressFormValue;
      });
      let ex = {
        ...extension,
        ...extensionLocation,
        shareholders:
          shareholders || this.personaGiuridica?.extension?.['shareholders'],
      };

      return this.pgService
        .edit(
          this.praticaProgressService.selectedPratica!.target,
          data,
          this.personaGiuridica?.id,
          //this.taxDataComponent?.tipoContabilita,
          undefined,
          this.kycComponent?.kycValue,
          this.addressFormComponentList?.get(0)?.addressFormValue,
          ex
        )
        .pipe(
          switchMap((res) => {
            if (file) {
              return this.documentsService.multiUpload(
                {
                  'Documentazione a supporto indicazione numero dipendenti impresa':
                    file,
                },
                this.personaGiuridica?.id,
                undefined,
                this.idPratica
              );
            } else return of(res);
          })
        );
    } else {
      return of(null);
    }
  }

  updateContabilita(event: 'Ordinaria' | 'Semplificata') {
    this.pgService
      .edit(
        this.praticaProgressService.selectedPratica!.target,
        {},
        this.personaGiuridica!.id,
        event
      )
      .subscribe(() => {
        this.personaGiuridica = {
          ...this.personaGiuridica!,
          tipoContabilita: event,
        };
      });
  }

  onEditShareholders(event: Shareholder[]) {
    this.pgService
      .edit(
        this.praticaProgressService.selectedPratica!.target,
        {},
        this.personaGiuridica!.id,
        undefined,
        undefined,
        undefined,
        {
          ...this.personaGiuridica?.extension,
          shareholders: event,
        }
      )
      .subscribe((res) => {
        this.personaGiuridica = res;
        this.cdr.detectChanges();
        this.dialogService.onSuccess();
      });
  }

  private performChecks() {
    this.cdr.detectChanges();
    this.smartFormComponentList?.forEach((c) => c.form.markAllAsTouched());
    if (this.praticaProgressService.isFrozenOrConfermata) {
      this.smartFormComponentList?.forEach((c) => c.form.disable());
      this.addressFormComponentList?.forEach((c) => c?.placesCtrl.disable());
    }
    let nDipendentiField = SfUtils.getField<Impresa>(
      'numeroDipendenti',
      this.fields1
    )?.controlRef;
    nDipendentiField?.valueChanges.subscribe((value) => {
      let docField = SfUtils.getField<Impresa>(
        'documentazioneNumeroDipendenti',
        this.fields1
      )?.controlRef;
      if (
        value !== this.personaGiuridica?.numeroDipendenti ||
        nDipendentiField?.hasError('min')
      ) {
        docField?.enable();
      } else {
        docField?.disable();
      }
    });
    let f = this.smartFormComponentList?.get(0)?.form;
    if (this.target === Target.IMPRESE) {
      f?.addValidators(crifValidator(this.personaGiuridica as Impresa));
    }
  }

  private interceptErrors(err: any) {
    {
      if (this.praticaProgressService.isFrozen === false) {
        return this.dialogService.manageError(err).pipe(
          tap(() => {
            this.praticaProgressService.isFrozen = true;
          })
        );
      } else {
        return of(null);
      }
    }
  }

  private saveProperty(data: Partial<Property>, propertyId?: string) {
    return (
      propertyId
        ? this.propertyService.edit(
            data,
            propertyId,
            undefined,
            this.personaGiuridica?.id
          )
        : this.propertyService.create(
            data,
            undefined,
            this.personaGiuridica?.id
          )
    ).pipe(
      tap((res) => this.dialogService.onSuccess()),
      switchMap((_) => this.updatePropertyTable())
    );
  }

  private updatePropertyTable() {
    return this.propertyService
      .getByPersonaGiuridica(this.personaGiuridica!.id)
      .pipe(
        tap((res) => {
          this.properties = res;
          this.calcTotalPercentage();
          this.cdr.detectChanges();
        })
      );
  }

  private openConfirmDialog() {
    return this.dialog
      .open(MessageDialogComponent, new ConfirmDialogConfig())
      .afterClosed();
  }

  private onDelete(productId: string) {
    return this.propertyService.delete(productId).pipe(
      tap((res) => {
        this.dialogService.onSuccess();
      }),
      switchMap((_) => this.updatePropertyTable())
    );
  }

  private buildForms(res: PersonaGiuridica) {
    if (this.target === Target.IMPRESE) {
      let impresa = res as Impresa;
      this.impresaValidator = crifValidator(impresa);
      this.consentFields = getConsentFields(impresa);
      this.fields1 = getApplicantFields(
        this.praticaProgressService.selectedPratica!.type,
        impresa
      );
    } else {
      this.addressFormComponentList?.forEach((c) =>
        c?.placesCtrl.markAsTouched()
      );
      [this.fields1, this.fields2] = getLiberoProfessionistFields(
        res as LiberoProfessionista,
        this.type
      );
    }
    let { type, tipoSoggetto } = this.praticaProgressService.selectedPratica!;
    return forkJoin([
      this.cmsService.getKyc(res?.kic),
      this.cmsService.getExtension(
        type,
        tipoSoggetto,
        'RICHIEDENTE',
        res.extension
      ),
    ]).pipe(
      tap(([kyc, extension]) => {
        this.kycFields = kyc;
        this.type === 'AML' && this.addAmlCustomSections(extension, res);
        this.sections = extension.sezioni;
        this.sections
          .filter((s) => s.type === 'ADDRESS')
          .forEach((s) => {
            this.extensionsLocationFieldsToBeShown[
              s.fields[0][0].formControlName
            ] = getExtensionLocationFields(this.type);
          });
        this.cdr.detectChanges();
        this.type === 'AML' && this.subscribeToAtecoChanges();
      })
    );
  }

  addAmlCustomSections(
    extension: {
      sezioni: MappedCmsSectionWithFieldList[];
    },
    pg: PersonaGiuridica
  ) {
    let additionalFields: FormFieldList = [
      [
        new AttivitaSvoltaField({
          value: pg?.extension?.['attivitaSvolta'],
        }),
        new AreaGeograficaField({
          value: pg?.extension?.['areaGeograficaAttivita'],
        }),
      ],
      [],
    ];
    if (pg?.tipoSoggetto !== 'LP') {
      additionalFields.push([
        new SfSelectFormField({
          formControlName: 'paesiCollegati',
          label:
            'Paesi collegati in maniera importante con la struttura proprietaria o di controllo',
          options: getCountriesOptions(),

          value: this.personaGiuridica?.extension?.['paesiCollegati'],
          required: true,
          isMulti: true,
        }),
      ]);
      additionalFields.unshift([
        new SfSelectFormField({
          formControlName: 'intermediarioBancarioFinanziario',
          label: 'Entità a basso rischio',
          options: [...INTERMEDIARIO_OPTIONS],
          value: pg?.extension?.['intermediarioBancarioFinanziario'],
        }),
        new SfSelectFormField({
          formControlName: 'naturaGiuridica',
          label: 'Natura giuridica',
          required: true,
          options: [...NATURA_GIURIDICA_OPTIONS],
          value: pg?.extension?.['naturaGiuridica'],
        }),
      ]);
    }
    extension.sezioni.splice(1, 0, {
      title: '',
      subtitle: '',
      description: '',
      type: '',
      fields: additionalFields,
    });
  }

  subscribeToAtecoChanges() {
    this.applicantForms
      ?.get(0)
      ?.form.get('ateco')
      ?.valueChanges.pipe(debounceTime(500))
      .subscribe((val) => {
        let intermediarioBancarioFinanziario = this.extensionsForms
          ?.find((f) => !!f.form.get('intermediarioBancarioFinanziario'))
          ?.form.get('intermediarioBancarioFinanziario');
        let atecoCode = ((val || '') as string).replaceAll('.', '');
        if (atecoCode.length === 4) {
          atecoCode = atecoCode + '0';
        }
        if (
          ATECO_ATTIVITA_FINANZIARIE_ASSICURATIVE.includes(atecoCode) &&
          !this.praticaProgressService.isFrozenOrConfermata
        ) {
          intermediarioBancarioFinanziario?.enable();
        } else {
          if (!this.praticaProgressService.isFrozenOrConfermata) {
            intermediarioBancarioFinanziario?.setValue(null);
          }
          intermediarioBancarioFinanziario?.disable();
        }
      });
  }

  setPanelState(state: boolean) {
    this.panelOpenState = state;
  }

}
